import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import {
  Desktop,
  LargeTablet,
  SmallTablet,
  Mobile,
} from "../MediaQuery/OnboardingPageMediaQuery.js";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import Text from "../Components/Text.jsx";
import WidthInterval from "../Components/WidthInterval.jsx";
import HeightInterval from "../Components/HeightInterval.jsx";
import { Helmet } from "react-helmet-async";
import useStore from "../store.js";
import config from "../config.js";
import StatisticsSection from "./OnboardingPageComponents/StatisticsSection.jsx";
import LandingTrust from "../Components/LandingTrust.jsx";
import WhoAreWeSection from "./OnboardingPageComponents/WhoAreWeSection.jsx";
import WhyDoesItMatterSection from "./OnboardingPageComponents/WhyDoesItMatterSection.jsx";
import WhatDoWeProvideSection from "./OnboardingPageComponents/WhatDoWeProvideSection.jsx";
import OurPromisesSection from "./OnboardingPageComponents/OurPromisesSection.jsx";
import ProblemSolvingSection from "./OnboardingPageComponents/ProblemSolvingSection.jsx";
import ModalTermsOfService from "../Components/Modal/ModalTermsOfService.jsx";
import ModalPrivacyPolicy from "../Components/Modal/ModalPrivacyPolicy.jsx";
import {
  buildLocalDevLoginURL,
  buildOAuthStartURL,
} from "../utils/oauth.js";
import { lockDocumentScroll } from "../utils/documentScrollLock.js";
import { landingEntryPath } from "../utils/landingExample.js";
import { getLandingExampleQuery } from "../utils/mailAttribution.js";
import GuestChatExperience from "../Components/GuestChatExperience.jsx";
import LandingExperiment from "../Components/LandingExperiment.jsx";
import { getLandingPreview } from "../utils/landingTournament.js";

const Onboarding = () => {
  const location = useLocation();
  const initialTextValue = getLandingExampleQuery(location.search);
  const guestDraftRef = useRef(null);
  // Keep the open login dialog when responsive branches replace their content.
  const [loginState, setLoginState] = useState(() => ({
    intent: useStore.getState().guestLoginReason || null,
    preparing: false,
    error: "",
  }));
  const openLogin = () => setLoginState((current) => ({ ...current, intent: "continue", error: "" }));
  // 쿠키 확인 및 리디렉션
  const navigate = useNavigate();
  const {
    updateUserInfo,
    resetGuestChatState,
    fetchGuestChatAvailability,
    guestLoginReason,
  } = useStore();

  const isLoginOverlayOpen = loginState.intent !== null || Boolean(guestLoginReason);
  useLayoutEffect(() => {
    if (!isLoginOverlayOpen) return undefined;
    return lockDocumentScroll();
  }, [isLoginOverlayOpen]);

  useEffect(() => {
    resetGuestChatState();
    fetchGuestChatAvailability();
  }, [fetchGuestChatAvailability, resetGuestChatState]);

  // 반응형 분기 전환 시 이전 화면의 ref 정리가 현재 화면의 ref를 덮지 않도록 분리한다.
  const desktopWhatDoWeProvideRef = useRef(null);
  const mobileWhatDoWeProvideRef = useRef(null);

  // 로그인 정보가 있을 경우
  // 온보딩 페이지에서 메인 페이지로 곧바로 리다이렉트
  useEffect(() => {
    const checkLoginStatus = async () => {
      try {
        await updateUserInfo(() => {}); // 빈 함수 전달 (모달 표시 안함)
        // updateUserInfo가 성공했고 실제로 userInfo가 있는지 확인
        const currentUserInfo = useStore.getState().userInfo;
        if (currentUserInfo && !getLandingPreview()) {
          // 메인 페이지에서 trackLogin 처리하므로 여기서는 호출하지 않음
          navigate(landingEntryPath("/main", location.search));
        } 
      } catch (error) {
      }
    };

    checkLoginStatus();
  }, [navigate, updateUserInfo, location.search]);

  /* Logo PC, Mobile 변경 */
  const [isTablet, setIsTablet] = React.useState(window.innerWidth <= 960);

  /* 이용약관 모달창 */
  const [isTermsModalOpen, setTermsModalIsOpen] = useState(false);
  const handleOpenTermsModal = () => {
    //setTermsModalIsOpen(true);
    window.location.href = `${window.location.origin}/termsofservice`;
  };
  const handleCloseTermsModal = () => {
    setTermsModalIsOpen(false);
  };

  /* 개인정보 처리방침 모달창 */
  const [isPrivacyModalOpen, setPrivacyModalIsOpen] = useState(false);
  const handleOpenPrivacyModal = () => {
    // setPrivacyModalIsOpen(true);
    window.location.href = `${window.location.origin}/privacypolicy`;
  };
  const handleClosePrivacyModal = () => {
    setPrivacyModalIsOpen(false);
  };

  /* SNS 로그인: state를 백엔드에서 발급·저장한 뒤 provider로 리다이렉트한다. */
  const beginOAuthLogin = (provider) => {
    const localDevLoginURL = buildLocalDevLoginURL({
      serverUri: config.serverUri,
      frontendOrigin: window.location.origin,
      hostname: window.location.hostname,
      appEnv: process.env.REACT_APP_ENV,
    });
    if (localDevLoginURL) {
      window.location.assign(localDevLoginURL);
      return;
    }

    const referralCode = sessionStorage.getItem("referralCode");
    window.location.assign(
      buildOAuthStartURL({
        provider,
        serverUri: config.serverUri,
        hostname: window.location.hostname,
        referralCode,
      })
    );
  };

  const handleResize = () => {
    setIsTablet(window.innerWidth <= 959); // Tablet / 1399 - 580
  };

  React.useEffect(() => {
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // 방문 집계는 후보/반응형 분기 밖 공통 진입에서 한 번만 수행한다.
  const [statistics, setStatistics] = useState({
    casesCount: 0,
    acceptedAnswerCount: 0,
    acceptRate: 0,
  });
  const statisticsRequest = useRef(null);
  useEffect(() => {
    if (new URLSearchParams(location.search).get("internal_test") === "1") return undefined;
    // StrictMode의 effect 재실행도 이미 시작한 요청을 공유한다.
    if (!statisticsRequest.current) {
      statisticsRequest.current = (async () => {
        try {
          const response = await fetch(`${config.serverUri}/counts`, {
            method: "GET",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
          });
          if (!response.ok) throw new Error("Failed to fetch statistics");
          return await response.json();
        } catch (error) {
          console.error(error);
          return null;
        }
      })();
    }
    let active = true;
    statisticsRequest.current.then((data) => {
      if (active && data) setStatistics(data);
    });
    return () => { active = false; };
  }, [location.search]);
  const casesCount = statistics.casesCount;

  return (
    // <Stop />
    <>
      <Helmet>
        <title>
          로우로우 Lawlow - 대법원 판례 검색 쉽게 한번에
        </title>
        <meta
          name="description"
          content="나에게 꼭 필요한 법률정보를 얻어가세요! 법원의 판결, 공통점, 차이점까지 내 상황과 비슷한 판례를 한눈에!"
        />
        <meta
          name="keywords"
          content="변호사, 변호사 추천, 선임비용, 무료법률상담, 전화법률상담, 법률상담, 법, 법률, 사기, 이혼, 범죄, 채무"
        />
      </Helmet>
      <Desktop>
        <GuestChatExperience initialTextValue={initialTextValue} sharedDraftRef={guestDraftRef} onProviderLogin={beginOAuthLogin} loginState={loginState} onLoginStateChange={setLoginState} />
        {/* 공통 첫 화면 → 공통 후기 → 후보별 중간 랜딩 → 공통 FAQ → 회사 정보 */}
        <LandingTrust />
        <LandingExperiment scrollRef={desktopWhatDoWeProvideRef} onLogin={openLogin}>
        <WhatDoWeProvideSection ref={desktopWhatDoWeProvideRef} />
        <OurPromisesSection casesCount={casesCount} />
        <StatisticsSection statistics={statistics} />
        <WhoAreWeSection />
        <ProblemSolvingSection />
        </LandingExperiment>
        <WhyDoesItMatterSection />
        <FooterDesktop>
          <FooterInfo>
            <FooterLeftSide>
              <Text
                size="20px"
                color="rgba(0, 0, 0, 0.30)"
                weight="700"
                height="normal">
                Lawlow
              </Text>
              <HeightInterval height={12} />
              <Text
                size="11px"
                color="rgba(0, 0, 0, 0.30)"
                weight="500"
                height="normal">
                고객센터: lawlow.korea@gmail.com ㅣ 대표: 조준영 ㅣ 대표 연락처:
                070-8080-0309 | 상호: 로우로우(Lawlow)
                <HeightInterval height={8} />
                사업자 등록번호: 302-57-00671 ㅣ사업장 소재지: 서울특별시
                영등포구 여의대방로43길 11-1 (신길동)
                <HeightInterval height={8} />
                민원담당자: 조준영ㅣ민원실: 070-8080-0309ㅣ 개인정보관리책임자:
                김훈기
                <HeightInterval height={8} />
                통신판매업: 2024-서울영등포-1585
              </Text>
            </FooterLeftSide>
            <FooterRightSide>
              <ModalsTermsInquiry>
                {isTermsModalOpen && (
                  <ModalTermsOfService
                    isOpen={isTermsModalOpen}
                    onClose={handleCloseTermsModal}
                  />
                )}
                {isPrivacyModalOpen && (
                  <ModalPrivacyPolicy
                    isOpen={isPrivacyModalOpen}
                    onClose={handleClosePrivacyModal}
                  />
                )}
                {/*  클릭 시 setModalIsOpen 상태 변경 함수 호출(모달 컴포넌트는 onClick() 호출 앞에 있어야함) */}
                <ModalText onClick={() => handleOpenTermsModal(true)}>
                  이용약관↗
                </ModalText>
                <ModalText $strong onClick={() => handleOpenPrivacyModal(true)}>
                  개인정보 처리방침↗
                </ModalText>
              </ModalsTermsInquiry>
              <HeightInterval height={16} />
              <Text
                size="11px"
                color="rgba(0, 0, 0, 0.30)"
                weight="500"
                height="normal">
                LAWLOW에서 운영되는 사이트 내 판매되는 모든 상품은 LAWLOW에서
                책임지고 있습니다.
                <HeightInterval height={8} />
                LAWLOW의 사전 서면 동의 없이 사이트의 일체의 정보, 콘텐츠 및 UI
                등을 상업적 목적으로 전재, 전송, 스크래핑 등 무단 사용할 수
                없습니다.
                <HeightInterval height={29} />
                Copyright ⓒ 2024 Lawlow Inc. All right reserved.
              </Text>
            </FooterRightSide>
          </FooterInfo>
        </FooterDesktop>
        {/* </Wrapper> */}
      </Desktop>

      <Mobile>
        {/* PWA */}
        {/* <ModalAddToHomeScreenPrompt
          isOpen={isAndroidPWAModalOpen}
          onClose={() => setAndroidPWAModalIsOpen(false)}
        /> */}
        {/* <ModalIOSAddToHomeScreen
          isOpen={isiOSPWAModalOpen}
          onClose={() => setiOSPWAModalIsOpen(false)}
        /> */}
        <GuestChatExperience mobile initialTextValue={initialTextValue} sharedDraftRef={guestDraftRef} onProviderLogin={beginOAuthLogin} loginState={loginState} onLoginStateChange={setLoginState} />
        <LargeTablet>
        </LargeTablet>
        <SmallTablet>
        </SmallTablet>
        {/* 모바일 */}
        <LandingTrust />
        <LandingExperiment scrollRef={mobileWhatDoWeProvideRef} onLogin={openLogin}>
        <WhatDoWeProvideSection ref={mobileWhatDoWeProvideRef} />
        <OurPromisesSection casesCount={casesCount} />
        <StatisticsSection statistics={statistics} />
        <WhoAreWeSection />
        <ProblemSolvingSection />
        </LandingExperiment>
        <WhyDoesItMatterSection />
        <FooterMobile>
          <TextContainer>
            <TextWrapper>
              <Text
                size="18px"
                color="rgba(0, 0, 0, 0.30)"
                weight="700"
                line-height="normal">
                Lawlow
              </Text>
              <WidthInterval width={12} />
              {isTermsModalOpen && (
                <ModalTermsOfService
                  isOpen={isTermsModalOpen}
                  onClose={handleCloseTermsModal}
                />
              )}
              {isPrivacyModalOpen && (
                <ModalPrivacyPolicy
                  isOpen={isPrivacyModalOpen}
                  onClose={handleClosePrivacyModal}
                />
              )}
              {/*  클릭 시 setModalIsOpen 상태 변경 함수 호출(모달 컴포넌트는 onClick() 호출 앞에 있어야함) */}
              <ModalText onClick={() => handleOpenTermsModal(true)}>
                이용약관↗
              </ModalText>
              <ModalText $strong onClick={() => handleOpenPrivacyModal(true)}>
                개인정보 처리방침↗
              </ModalText>
            </TextWrapper>
            {!isTablet && (
              <IsTabletTextContainer>
                <br />
                <Text size="10px" color="rgba(0, 0, 0, 0.30)" weight="500">
                  Copyright ⓒ 2024 Lawlow Inc. All right reserved.
                </Text>
              </IsTabletTextContainer>
            )}
          </TextContainer>
          <HeightInterval height={14} />
          <TextContainer2>
            <Text
              size="10px"
              color="rgba(0, 0, 0, 0.30)"
              weight="500"
              height="18px">
              고객센터: lawlow.korea@gmail.com ㅣ대표 연락처: 070-8080-0309{" "}
              <br />
              사업자 등록번호: 302-57-00671 ㅣ상호: 로우로우(Lawlow) ㅣ대표:
              조준영
              <br />
              사업장 소재지: 서울특별시 영등포구 여의대방로43길 11-1 (신길동)
              <br />
              개인정보관리책임자: 김훈기ㅣ민원담당자: 조준영 ㅣ 민원실:
              070-8080-0309 <br /> 통신판매업: 2024-서울영등포-1585
            </Text>
            <HeightInterval height={16} />
            <Text
              size="9px"
              color="rgba(0, 0, 0, 0.30)"
              weight="500"
              height="16px">
              LAWLOW에서 운영되는 사이트 내 판매되는 모든 상품은 LAWLOW에서
              책임지고 있습니다. <br /> LAWLOW의 사전 서면 동의 없이 사이트의
              일체의 정보, 콘텐츠 및 UI 등을 상업적 목적으로
              <br /> 전재, 전송, 스크래핑 등 무단 사용할 수 없습니다.
            </Text>
            <HeightInterval height={7} />
            {isTablet && (
              <Text size="10px" color="rgba(0, 0, 0, 0.30)" weight="500">
                Copyright ⓒ 2024 LAWLOW Inc. All right reserved.
              </Text>
            )}
          </TextContainer2>
        </FooterMobile>
      </Mobile>
    </>
  );
};

const FooterMobile = styled.div`
  // width: 869px;
  height: 269px;
  bottom: 0; /* 화면 하단 고정 */
  display: flex;
  flex-direction: column;
  justify-content: center;
  line-height: 16px;
  // padding-left: 20px;

  @media (max-width: 1439px) and (min-width: 960px) {
    // width: calc(100% - 314px);
    flex-direction: row;
    // align-items: center;
    justify-content: center;
  }

  @media (max-width: 959px) and (min-width: 580px) {
    height: 269px;
    margin: 0 auto;
    padding: 0 calc((100vw - 320px) / 3); /* 좌우 패딩을 비례적으로 조정 */
  }

  @media (max-width: 579px) and (min-width: 340px) {
    flex-direction: column;
    padding-left: 24px;
  }

  @media (max-width: 339px) {
    flex-direction: column;
    padding-top: 24px;
    padding-left: 24px;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: row;

  @media (max-width: 1439px) {
    flex-direction: column;
    padding-top: 35px;
    padding-right: 35px;
  }
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const TextContainer2 = styled.div`
  @media (max-width: 1439px) {
    padding-top: 30px;
  }
`;

const IsTabletTextContainer = styled.div``;

// 처리방침은 다른 고지와 구분되게 진하고 굵게 보여준다(개인정보 보호법 시행령 제31조).
const ModalText = styled.div`
  display: inline-block;
  margin-right: 12px;
  color: ${({ $strong }) => ($strong ? "rgba(0, 0, 0, 0.6)" : "rgba(0, 0, 0, 0.3)")};
  font-size: 14px;
  font-weight: ${({ $strong }) => ($strong ? 700 : 500)};
  font-style: normal;
  line-height: normal;

  &:hover {
    cursor: pointer;
  }
`;


const FooterDesktop = styled.div`
  background: #fff;
  width: 100%;
  height: 156px;
`;

const FooterInfo = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding-top: 21px;
`;

const FooterLeftSide = styled.div`
  display: flex;
  flex-direction: column;
  width: 647px;
`;

const FooterRightSide = styled.div`
  display: flex;
  flex-direction: column;
  width: auto;
`;

const ModalsTermsInquiry = styled.div``;

export default Onboarding;
